import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import type { WidgetFeedbackContent } from "@/utils/widgetFeedbackLoader";
import LogsTab from "@/pages/Board/BugHurd/logs";

interface WidgetContentViewerProps {
  feedbackContent: WidgetFeedbackContent;
}

const WidgetContentViewer = ({ feedbackContent }: WidgetContentViewerProps) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const screenshots = [
    feedbackContent.screenshotUrl && {
      url: feedbackContent.screenshotUrl,
      label: t("Screenshot"),
    },
    feedbackContent.fullpageUrl && {
      url: feedbackContent.fullpageUrl,
      label: t("Full Page"),
    },
  ].filter(Boolean) as { url: string; label: string }[];

  const systemInfo = feedbackContent.metadata
    ? {
        url: feedbackContent.metadata.url,
        os: feedbackContent.metadata.os,
        browser: feedbackContent.metadata.browser,
        windowSize: feedbackContent.metadata.windowSize,
        timestamp: feedbackContent.metadata.timestamp,
        country: feedbackContent.metadata.country,
        timezone: feedbackContent.metadata.timezone,
      }
    : undefined;

  const hasDiagnosticLogs =
    (feedbackContent.consoleLogs && feedbackContent.consoleLogs.length > 0) ||
    (feedbackContent.networkLogs && feedbackContent.networkLogs.length > 0) ||
    (feedbackContent.actionLogs && feedbackContent.actionLogs.length > 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Customer Comment */}
      {feedbackContent.metadata?.comment && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.info.light}`,
            borderRadius: 1.5,
            p: 2,
            backgroundColor: theme.palette.info.main + "10",
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
          }}
        >
          <ChatBubbleOutlineIcon
            sx={{ color: theme.palette.info.main, mt: 0.25, fontSize: 20 }}
          />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {t("Customer Comment")}
            </Typography>
            <Box
              className="ql-editor"
              sx={{
                mt: 0.5,
                p: "0 !important",
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
                "& p": { margin: 0 },
                "& a": { color: theme.palette.primary.main },
                "& img": { maxWidth: "100%", borderRadius: 1 },
              }}
              dangerouslySetInnerHTML={{ __html: feedbackContent.metadata.comment }}
            />
          </Box>
        </Box>
      )}

      {/* Screenshots */}
      {screenshots.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t("Screenshots")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {screenshots.map((img) => (
              <Box
                key={img.label}
                sx={{
                  cursor: "pointer",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "hidden",
                  "&:hover": { opacity: 0.85 },
                }}
                onClick={() => setFullscreenImage(img.url)}
              >
                <img
                  src={img.url}
                  alt={img.label}
                  style={{
                    display: "block",
                    maxWidth: 240,
                    maxHeight: 160,
                    objectFit: "cover",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", py: 0.5 }}
                >
                  {img.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Video */}
      {feedbackContent.recordingUrl && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t("Recording")}
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: theme.palette.common.black,
            }}
          >
            <video
              src={feedbackContent.recordingUrl}
              controls
              style={{ width: "100%", maxHeight: 400, display: "block" }}
            />
          </Box>
        </Box>
      )}

      {/* Logs & Diagnostics — only shown when recording was captured */}
      {hasDiagnosticLogs && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t("Logs & Info")}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: "hidden",
              height: 400,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <LogsTab
              consoleLogs={feedbackContent.consoleLogs || []}
              networkLogs={feedbackContent.networkLogs || []}
              systemInfo={systemInfo}
              actionLogs={feedbackContent.actionLogs || []}
            />
          </Box>
        </Box>
      )}

      {/* System Info — shown standalone for image-only feedback */}
      {!hasDiagnosticLogs && systemInfo && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t("System Info")}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {systemInfo.url && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("URL")}:</strong> {systemInfo.url}
              </Typography>
            )}
            {systemInfo.browser && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("Browser")}:</strong> {systemInfo.browser}
              </Typography>
            )}
            {systemInfo.os && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("OS")}:</strong> {systemInfo.os}
              </Typography>
            )}
            {systemInfo.windowSize && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("Window Size")}:</strong> {systemInfo.windowSize}
              </Typography>
            )}
            {systemInfo.timestamp && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("Timestamp")}:</strong>{" "}
                {new Date(systemInfo.timestamp).toLocaleString()}
              </Typography>
            )}
            {systemInfo.country && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("Location")}:</strong> {systemInfo.country}
                {systemInfo.timezone ? ` (${systemInfo.timezone})` : ""}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Fullscreen Image Dialog */}
      <Dialog
        open={!!fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            maxHeight: "none",
            maxWidth: "none",
            borderRadius: 0,
            backgroundColor: "rgba(0, 0, 0, 0.95)",
          },
        }}
      >
        <Box
          sx={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 1400,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: "50%",
            backdropFilter: "blur(5px)",
          }}
        >
          <IconButton
            onClick={() => setFullscreenImage(null)}
            sx={{ color: "white" }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            p: 0,
            height: "100vh",
            width: "100vw",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.95)",
          }}
          onClick={() => setFullscreenImage(null)}
        >
          {fullscreenImage && (
            <img
              src={fullscreenImage}
              alt={t("Screenshot")}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "95vw",
                height: "95vh",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WidgetContentViewer;
